import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminApi, getApiErrorMessage } from '../services/adminApi'
import { Alert, Badge, Button, Card, EmptyState, Input, PageHeader, Spinner, Textarea } from '../components/ui'

function duration(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest ? `${hours}h ${rest}min` : `${hours}h`
}

function dateTime(value) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR')
}

const sourceLabels = {
  web_login: 'Login web',
  tenant_discovery: 'Busca de unidade',
  mobile_login: 'Login móvel',
  mfa: 'Confirmação MFA',
}

export default function LoginSecurity() {
  const [blocks, setBlocks] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [selected, setSelected] = useState(null)
  const [password, setPassword] = useState('')
  const [reason, setReason] = useState('')
  const [unlocking, setUnlocking] = useState(false)

  const load = useCallback(async (quiet = false) => {
    if (quiet) setRefreshing(true)
    else setLoading(true)
    setError('')
    try {
      const { data } = await adminApi.listLoginBlocks()
      setBlocks(data.data || [])
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível carregar os bloqueios.'))
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    load()
    const timer = window.setInterval(() => load(true), 30_000)
    return () => window.clearInterval(timer)
  }, [load])

  const totalAttempts = useMemo(
    () => blocks.reduce((sum, block) => sum + (Number(block.attempts) || 0), 0),
    [blocks],
  )

  async function unlock(event) {
    event.preventDefault()
    if (!selected || unlocking) return
    setUnlocking(true)
    setError('')
    setMessage('')
    try {
      const { data } = await adminApi.unlockLoginBlock(selected.id, { password, reason })
      setMessage(data.message || 'Acesso desbloqueado.')
      setSelected(null)
      setPassword('')
      setReason('')
      await load(true)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Não foi possível desbloquear.'))
    } finally {
      setUnlocking(false)
    }
  }

  if (loading) return <Spinner label="Carregando segurança de login…" />

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Segurança"
        title="Bloqueios de login"
        description="Dados sensíveis. Acesso exclusivo do super administrador e desbloqueios auditados."
        icon="fa-shield-halved"
        actions={<Button variant="outline" icon={refreshing ? 'fa-spinner fa-spin' : 'fa-rotate'} disabled={refreshing} onClick={() => load(true)}>Atualizar</Button>}
      />

      <Alert type="warning">
        Identificadores são mascarados. Para desbloquear, confirme sua senha atual e informe uma justificativa.
      </Alert>
      {error && <Alert type="error">{error}</Alert>}
      {message && <Alert type="success">{message}</Alert>}

      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <p className="text-sm font-medium text-slate-500">IPs bloqueados agora</p>
          <p className="mt-2 text-3xl font-bold text-brand-deep">{blocks.length}</p>
        </Card>
        <Card>
          <p className="text-sm font-medium text-slate-500">Tentativas acumuladas</p>
          <p className="mt-2 text-3xl font-bold text-brand-deep">{totalAttempts}</p>
        </Card>
      </div>

      <Card bodyClassName="!p-0">
        <div className="mp-scrollbar overflow-x-auto">
          <table className="w-full min-w-[1050px] text-left text-sm">
            <thead className="bg-slate-50/80">
              <tr className="border-b text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3.5">IP</th>
                <th className="px-5 py-3.5">Tenant</th>
                <th className="px-5 py-3.5">Identificador</th>
                <th className="px-5 py-3.5">Tentativas</th>
                <th className="px-5 py-3.5">Bloqueado há</th>
                <th className="px-5 py-3.5">Tempo restante</th>
                <th className="px-5 py-3.5">Última tentativa</th>
                <th className="px-5 py-3.5">Ação</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((block) => (
                <tr key={`${block.id}-${block.ip}`} className="border-b border-slate-100 align-top hover:bg-blue-50/30">
                  <td className="px-5 py-4 font-mono text-xs font-semibold text-slate-800">{block.ip}</td>
                  <td className="px-5 py-4">
                    <div className="flex max-w-56 flex-wrap gap-1">
                      {block.tenants?.length
                        ? block.tenants.map((tenant) => <Badge key={tenant} tone="info">{tenant}</Badge>)
                        : <span className="text-xs text-slate-500">Não identificado</span>}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="space-y-1 text-xs text-slate-600">
                      {(block.identifiers || []).map((identifier) => <div key={identifier}>{identifier}</div>)}
                      <div className="text-[11px] text-slate-400">{(block.sources || []).map((source) => sourceLabels[source] || source).join(', ') || 'Registro anterior'}</div>
                    </div>
                  </td>
                  <td className="px-5 py-4 font-semibold">{block.attempts}</td>
                  <td className="px-5 py-4">{duration(block.blocked_for_seconds)}</td>
                  <td className="px-5 py-4"><Badge tone="danger">{duration(block.remaining_seconds)}</Badge></td>
                  <td className="px-5 py-4 text-xs text-slate-600">{dateTime(block.last_attempt)}</td>
                  <td className="px-5 py-4">
                    <Button variant="danger" className="!px-3 !py-1.5 text-xs" onClick={() => { setSelected(block); setPassword(''); setReason(''); setError(''); setMessage('') }}>
                      Desbloquear
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {blocks.length === 0 && <EmptyState title="Nenhum bloqueio ativo" description="Não há IPs bloqueados pelo controle progressivo de login neste momento." icon="fa-shield-circle-check" />}
        </div>
      </Card>

      {selected && (
        <Card title="Confirmar desbloqueio" description={`IP ${selected.ip} · ${selected.tenants?.join(', ') || 'tenant não identificado'}`}>
          <form onSubmit={unlock} className="space-y-4">
            <Input
              label="Sua senha atual *"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
            />
            <Textarea
              label="Justificativa *"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              minLength={5}
              maxLength={500}
              required
              hint="A justificativa ficará registrada na auditoria de segurança."
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" disabled={unlocking} onClick={() => { setSelected(null); setPassword(''); setReason('') }}>Cancelar</Button>
              <Button type="submit" variant="danger" disabled={unlocking} icon={unlocking ? 'fa-spinner fa-spin' : 'fa-unlock-keyhole'}>
                {unlocking ? 'Desbloqueando…' : 'Confirmar desbloqueio'}
              </Button>
            </div>
          </form>
        </Card>
      )}
    </div>
  )
}
